import { hasSameDomain } from '@/helpers/apiHelpers/urlHelper';
import { To, useLocation, useNavigate } from 'react-router';
import { isString } from '@/helpers/objectHelpers';

type NavigationStateType = {
  pathname: string;
  search: string;
  backUrl: To;
};

type NavigationQueueType = Record<string, NavigationStateType>;

let NAVIGATION_QUEUE_STORAGE: NavigationQueueType = {};

const transformUrl = (url: string = '') => {
  return url.replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, ':id');
};

export const useStatefulNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateBack = (to: To) => {
    const hasBrowserHistory = window.history.length > 1;
    const hasExternalReferrer = document.referrer && !hasSameDomain(document.referrer);
    const hasPreviousLocation = hasBrowserHistory && !hasExternalReferrer;

    if (!hasPreviousLocation) {
      return navigate(to);
    }

    const currentUrl = isString(to) ? to : to.pathname;
    const navigationKey = transformUrl(currentUrl);
    const existingNavigationState = NAVIGATION_QUEUE_STORAGE[navigationKey];

    if (!existingNavigationState) {
      return navigate(to);
    }

    delete NAVIGATION_QUEUE_STORAGE[navigationKey];

    navigate({ pathname: existingNavigationState.pathname, search: existingNavigationState.search });
  };

  const navigateFrom = (to: To) => {
    if (!to) return;

    if (!location.search) {
      return navigate(to);
    }

    const pathname = location.pathname || '';
    const state = { pathname: location.pathname, search: location.search };
    const navigationKey = transformUrl(pathname);

    NAVIGATION_QUEUE_STORAGE = {
      ...NAVIGATION_QUEUE_STORAGE,
      [navigationKey]: { ...state, backUrl: to },
    };

    return navigate(to, { state });
  };

  const clearHistory = () => {
    NAVIGATION_QUEUE_STORAGE = {};
  };

  return {
    navigateBack,
    navigateFrom,
    clearHistory,
  };
};
